/**
 * UploadDocumentDialog — the shared "Dokument hochladen & analysieren" modal
 * (dms/spec.md §2.2, §4.1, §7). Presentational + stateful (the `NeueAufgabeDialog`
 * shell precedent): the parent injects `onUpload` (a live `apiClient.documents.upload`
 * call or a mock no-op) and invalidates the list on success.
 *
 * Reused from TWO entry points (S5): the Ablage header's "Dokument hochladen" button
 * AND the global header Upload button (AppShell) — same wizard, opened from anywhere.
 *
 * Multi-file + drag-and-drop (S5): the drop zone accepts several PDFs at once (click
 * to browse OR drag files in), capped at MAX_FILES. Each file is uploaded via the
 * SAME per-file `onUpload(file, opts)` contract, looped internally — so every parent
 * that wired a single-file `onUpload` keeps working unchanged. `title` is offered only
 * for a single file (a shared title for many files makes no sense; each file then uses
 * its own name); `docKind` applies to all selected files (usually "Automatisch erkennen").
 *
 * Multipart body mirrors `POST /documents/upload` (basic-user, `document.upload`
 * — already in BASIC_BASELINE_KEYS): `file` (required, application/pdf),
 * `doc_kind?`, `title?`. `linked_type`/`linked_id` are left unset here (the
 * generic Ablage upload; a pre-linked upload from a client/policy page is a
 * future entry point, not this one).
 *
 * [Source: apps/web/src/features/aufgaben/components/NeueAufgabeDialog.tsx] (modal shell)
 * [Source: apps/api/src/modules/documents/documents-actions.controller.ts#upload]
 */
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { AlertTriangle, FileText, Upload, UploadCloud, X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { cn } from '../../../lib/cn';
import { fileSize } from '../../../lib/format';
import type { DocExtractionKind } from '@brokly/shared';
import type { DocumentUploadResult } from '../../../lib/api-client';

const DOC_KIND_LABELS: Record<DocExtractionKind, string> = {
  policy: 'Police',
  cantonal_excerpt: 'Handelsregisterauszug',
  premium_invoice: 'Prämienrechnung',
  commission_statement: 'Courtageabrechnung',
  broker_agreement: 'Maklermandat',
};

/** Upper bound on one batch. A generous cap, not a business rule — keeps the UI + the
 *  sequential upload loop bounded. Confirm the number with Valerio if it needs raising. */
const MAX_FILES = 20;

/** True when the browser reports this file as a PDF, or (some OSes leave `type` empty on
 *  drop) the name ends in .pdf. The server re-checks the real Content-Type regardless. */
function isPdf(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

export interface UploadDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Perform ONE file's upload (parent wires the live/mock call). Resolves on success. */
  onUpload: (
    file: File,
    opts: { docKind?: DocExtractionKind; title?: string },
  ) => Promise<DocumentUploadResult>;
  /** Fired once ALL uploads in the batch succeed (parent invalidates the list + toasts). */
  onUploaded?: () => void;
}

type SubmitState = 'idle' | 'saving' | 'failed';

export function UploadDocumentDialog({
  open,
  onOpenChange,
  onUpload,
  onUploaded,
}: UploadDocumentDialogProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [title, setTitle] = useState('');
  const [docKind, setDocKind] = useState<DocExtractionKind | ''>('');
  const [state, setState] = useState<SubmitState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setFiles([]);
      setTitle('');
      setDocKind('');
      setState('idle');
      setErrorMsg(null);
      setIsDragging(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && state !== 'saving') onOpenChange(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange, state]);

  /** Merge new files in, keep only PDFs, de-dupe by name+size, cap at MAX_FILES.
   *  Computed from `files` (current for a single user action) — no side effect inside the
   *  state updater (which would run twice under StrictMode). */
  function addFiles(incoming: FileList | File[]) {
    const list = Array.from(incoming);
    const pdfs = list.filter(isPdf);
    const rejectedNonPdf = list.length - pdfs.length;
    const merged = [...files];
    for (const f of pdfs) {
      if (!merged.some((m) => m.name === f.name && m.size === f.size)) merged.push(f);
    }
    const overflow = merged.length > MAX_FILES;
    setFiles(merged.slice(0, MAX_FILES));
    if (overflow) {
      setErrorMsg(`Maximal ${MAX_FILES} Dateien pro Vorgang, die übrigen wurden ignoriert.`);
    } else if (rejectedNonPdf > 0) {
      setErrorMsg('Nur PDF-Dateien werden akzeptiert, andere Dateien wurden ignoriert.');
    } else {
      setErrorMsg(null);
    }
    if (state === 'failed') setState('idle');
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    if (state === 'saving') return;
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }

  const canSave = files.length > 0 && state !== 'saving';

  async function handleSubmit() {
    if (files.length === 0) return;
    setState('saving');
    setErrorMsg(null);
    const failed: File[] = [];
    const total = files.length;
    for (const file of files) {
      try {
        await onUpload(file, {
          ...(docKind ? { docKind } : {}),
          // A shared title only makes sense for a single file; many files each keep their name.
          ...(total === 1 && title.trim() ? { title: title.trim() } : {}),
        });
      } catch {
        failed.push(file);
      }
    }
    if (failed.length === 0) {
      onUploaded?.();
      onOpenChange(false);
      return;
    }
    setErrorMsg(
      failed.length === total
        ? 'Kein Dokument konnte hochgeladen werden.'
        : `${failed.length} von ${total} Dateien fehlgeschlagen: ${failed.map((f) => f.name).join(', ')}`,
    );
    setState('failed');
    // Keep only the failed files so the user can retry just those.
    setFiles(failed);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-xl border bg-card shadow-lg">
        <div className="flex items-start justify-between gap-2 border-b p-4">
          <div className="flex flex-col gap-0.5 pr-4">
            <h2 className="flex items-center gap-2 font-semibold text-foreground">
              <Upload className="size-4 text-primary" />
              Dokument hochladen &amp; analysieren
            </h2>
            <p className="text-xs text-muted-foreground">
              PDF(s) hochladen: werden automatisch klassifiziert &amp; extrahiert.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label="Schliessen"
            className="size-7 shrink-0"
          >
            <X className="size-4" />
          </Button>
        </div>

        <form
          id="upload-document-form"
          className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto p-4"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          {/* Drag-and-drop zone (S5): a clearly-marked drop target that ALSO opens the OS
              file picker on click. `multiple` allows selecting several PDFs at once. */}
          <div
            role="button"
            tabIndex={0}
            aria-label="Dateien hierher ziehen oder klicken zum Auswählen"
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (state !== 'saving') setIsDragging(true);
            }}
            onDragLeave={(e) => {
              // Only clear when the cursor leaves the zone entirely, not when it crosses a
              // child element (which would make the highlight flicker).
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsDragging(false);
            }}
            onDrop={onDrop}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors',
              isDragging
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:bg-accent/40',
            )}
          >
            <UploadCloud className="size-6 text-primary" />
            <span className="text-sm font-medium text-foreground">
              PDF(s) hierher ziehen
            </span>
            <span className="text-xs">
              oder <span className="text-primary underline">klicken zum Auswählen</span> · bis zu{' '}
              {MAX_FILES} Dateien
            </span>
            <input
              ref={inputRef}
              id="upload-document-file"
              type="file"
              accept="application/pdf"
              multiple
              tabIndex={-1}
              aria-label="PDF-Datei auswählen"
              className="sr-only"
              onChange={(e) => {
                if (e.target.files?.length) addFiles(e.target.files);
                // reset so selecting the same file again re-triggers change
                e.target.value = '';
              }}
            />
          </div>

          {files.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${f.size}-${i}`}
                  className="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm"
                >
                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-foreground">{f.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{fileSize(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    disabled={state === 'saving'}
                    aria-label={`${f.name} entfernen`}
                    className="grid size-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <X className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* A title only makes sense for a single file; for many, each keeps its own name. */}
          {files.length === 1 && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="upload-document-title" className="text-xs text-muted-foreground">
                Titel (optional)
              </label>
              <input
                id="upload-document-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Wird sonst vom Dateinamen übernommen"
                className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label htmlFor="upload-document-kind" className="text-xs text-muted-foreground">
              Dokumentart (optional{files.length > 1 ? ', gilt für alle' : ''})
            </label>
            <select
              id="upload-document-kind"
              value={docKind}
              onChange={(e) => setDocKind(e.target.value as DocExtractionKind | '')}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Automatisch erkennen</option>
              {(Object.entries(DOC_KIND_LABELS) as Array<[DocExtractionKind, string]>).map(
                ([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ),
              )}
            </select>
          </div>

          {errorMsg && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-sem-alert/40 bg-sem-alert-bg px-3 py-2 text-sm text-sem-alert"
            >
              <AlertTriangle className="size-4 shrink-0" />
              {errorMsg}
            </div>
          )}
        </form>

        <div className="flex items-center justify-end gap-2 border-t p-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={state === 'saving'}>
            Abbrechen
          </Button>
          <Button type="submit" form="upload-document-form" disabled={!canSave}>
            <Upload className="size-4" />
            {state === 'saving'
              ? 'Lädt hoch …'
              : files.length > 1
                ? `${files.length} hochladen`
                : 'Hochladen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
